import { useState } from 'react'
import SectionCard from '@/components/dashboard/SectionCard'
import StatTile from '@/components/dashboard/StatTile'
import { EmptyState } from '@/components/dashboard/BarListWidget'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Wallet, Lock, Download, Loader2 } from 'lucide-react'
import {
  myPayslips, viewMyPayslip, downloadPayslipPdf, MONTHS,
  type MyPayslipSummary, type Payslip,
} from '@/api/payroll'

function money(v: number | string | null | undefined, currency = 'PKR'): string {
  const n = Number(v ?? 0)
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

export default function Payslips() {
  const { data: slips = [], isLoading } = useQuery({ queryKey: ['payslips', 'mine'], queryFn: myPayslips })

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Wallet className="h-6 w-6 text-brand" /> My Payslips</h1>
        <p className="text-sm text-gray-500">Your finalized monthly payslips. Unlock each with the last 5 digits of your CNIC.</p>
      </div>

      {slips.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <StatTile label="Payslips" value={slips.length} tone="blue" />
          <StatTile
            label="Latest"
            value={slips[0]?.run_month ? `${MONTHS[slips[0].run_month]} ${slips[0].run_year ?? ''}` : '—'}
            tone="green"
          />
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : slips.length === 0 ? (
        <SectionCard title="Payslips"><EmptyState label="No payslips yet." /></SectionCard>
      ) : (
        <div className="space-y-3">
          {slips.map((s: MyPayslipSummary) => <PayslipCard key={s.id} summary={s} />)}
        </div>
      )}
    </div>
  )
}

function PayslipCard({ summary }: { summary: MyPayslipSummary }) {
  const [code, setCode] = useState('')
  const [payslip, setPayslip] = useState<Payslip | null>(null)
  const [error, setError] = useState('')

  const unlockMut = useMutation({
    mutationFn: () => viewMyPayslip(summary.id, code),
    onSuccess: (p) => { setPayslip(p); setError('') },
    onError: (e: any) => setError(e?.response?.status === 403 ? 'Incorrect code.' : (e?.response?.data?.detail || 'Could not unlock.')),
  })
  const pdfMut = useMutation({
    mutationFn: () => downloadPayslipPdf(summary.id, `payslip-${summary.run_year}-${String(summary.run_month).padStart(2, '0')}`),
    onError: (e: any) => setError(e?.response?.data?.detail || 'PDF download failed.'),
  })

  const period = `${summary.run_month ? MONTHS[summary.run_month] : ''} ${summary.run_year || ''}`.trim()

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-900">{period}</h2>
        {payslip ? (
          <span className="text-lg font-bold text-gray-900">{money(payslip.net, payslip.currency)}</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3.5 w-3.5" /> Locked</span>
        )}
      </div>

      {!payslip ? (
        <form
          onSubmit={(e) => { e.preventDefault(); if (code.trim()) unlockMut.mutate() }}
          className="flex flex-wrap items-center gap-2"
        >
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError('') }}
            placeholder="Last 5 digits of CNIC"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40"
          />
          <button type="submit" disabled={unlockMut.isPending || !code.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-brand hover:bg-brand-dark px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
            {unlockMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Unlock
          </button>
          {error && <span className="text-sm text-red-600">{error}</span>}
        </form>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-sm">
            <Row label="Basic" value={money(payslip.basic, payslip.currency)} />
            <Row label={`Overtime (${Number(payslip.overtime_hours)} hrs)`} value={money(payslip.overtime_earning, payslip.currency)} />
            <Row label="Medical Reimb." value={money(payslip.medical_reimbursement, payslip.currency)} />
            <Row label="Commission" value={money(payslip.commission, payslip.currency)} />
            <Row label="Adjustment" value={money(payslip.adjustment, payslip.currency)} />
            <Row label="Gross Pay" value={money(payslip.gross, payslip.currency)} />
            <Row label="Provident Fund" value={money(payslip.provident_fund, payslip.currency)} />
            <Row label="Loan Deducted" value={money(payslip.loan_deducted, payslip.currency)} />
            <Row label="Food" value={money(payslip.food, payslip.currency)} />
            <Row label="Gym" value={money(payslip.gym_membership, payslip.currency)} />
            <Row label="WHT" value={money(payslip.wht, payslip.currency)} />
            <Row label="Total Deduction" value={money(payslip.total_deduction, payslip.currency)} />
            <Row label="Net Pay" value={money(payslip.net, payslip.currency)} strong />
            <Row label="Present / Working" value={`${payslip.present_days} / ${payslip.working_days} days`} />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button onClick={() => pdfMut.mutate()} disabled={pdfMut.isPending} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              {pdfMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </button>
            <span className="text-xs text-gray-400">PDF opens with the same last-5 CNIC code.</span>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </>
      )}
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={strong ? 'text-gray-900 font-bold' : 'text-gray-800 font-medium'}>{value}</p>
    </div>
  )
}
